import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { DotsThree, DownloadSimple, FileMagnifyingGlass, X } from "@phosphor-icons/react";
import {
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  PageHeader,
  Select,
  Table,
  TBody,
  TD,
  TDNum,
  TFoot,
  TH,
  THead,
  TR,
  cn,
  formatCount,
  useSortedRows,
} from "@ghezaa/ui";
import { useDebounced } from "@ghezaa/shell";
import type { ReportCatalogue, ReportColumn, ReportDimension, ReportRow, ReportSpec, ReportTable, ReportValue } from "@/api/client";
import { useCreateView, useDeleteView, useShareView, useUpdateView } from "@/api/mutations";
import { reportPath, useReport, useReportCatalogue, useReportValues, useReportView } from "@/api/queries";
import { day } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";
import { attainmentFits, daysOf, figure, header, isTime, newSpec, sameSpec, specOf, tidy } from "@/lib/reports";

export type ReportSearch = { spec?: ReportSpec; starter?: string };

export function reportSearch(search: Record<string, unknown>): ReportSearch {
  const spec = specOf(search.spec);
  return {
    ...(spec ? { spec } : {}),
    ...(typeof search.starter === "string" && search.starter !== "" ? { starter: search.starter } : {}),
  };
}

/** A report not saved yet: a new one, or a starting point opened. */
export function NewReportPage() {
  return <ReportPage id={undefined} />;
}

export function SavedReportPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  return <ReportPage id={id} key={id} />;
}

/**
 * One report: its definition in controls above the table, kept in the address so a report is a link and Back undoes a
 * change; the table as the server worked it out, sortable on the page; saving, sharing and export in the header.
 */
function ReportPage({ id }: { id: string | undefined }) {
  const market = useCurrentMarket();
  const search = useSearch({ strict: false }) as ReportSearch;
  const navigate = useNavigate();
  const catalogue = useReportCatalogue(market.code);
  const view = useReportView(market.code, id);
  const starter = catalogue.data?.starters.find((s) => s.key === search.starter);
  const saved = view.data?.spec ?? undefined;
  const spec = search.spec ?? saved ?? (id ? undefined : (starter?.spec ?? newSpec));
  const report = useReport(market.code, spec ?? null);
  const [naming, setNaming] = useState<"new" | "rename" | null>(null);
  const [deleting, setDeleting] = useState(false);

  const create = useCreateView(market.code);
  const update = useUpdateView(market.code, id ?? "");
  const share = useShareView(market.code, id ?? "");
  const remove = useDeleteView(market.code);

  const mine = Boolean(view.data?.mine);
  const changed = id ? !sameSpec(spec, saved) : true;
  // A starting point changed is a new report; its name is offered again when it is saved.
  const title = view.data?.name ?? (starter && sameSpec(spec, starter.spec) ? starter.name : id ? "Report" : "New report");
  const setSpec = (next: ReportSpec) => void navigate({ to: ".", search: (s: ReportSearch) => ({ ...s, spec: tidy(next) }) as never, replace: true });

  if (id && view.isError) {
    return (
      <EmptyState
        icon={FileMagnifyingGlass}
        title="This report is not here"
        description="It was deleted, or it is no longer shared with you."
        action={
          <Button asChild>
            <Link to="/$market/reports" params={{ market: market.code }}>
              Open Reports
            </Link>
          </Button>
        }
      />
    );
  }

  const table = report.data;
  const c = catalogue.data;
  const periodLabel = c?.periods.find((p) => p.key === spec?.period)?.label ?? "";
  const description = table
    ? `${periodLabel}, ${daysOf(table.now)}${table.before ? `, against ${daysOf(table.before)}` : ""}${table.everyone ? "" : ` · ${table.codes.join(", ") || "no code of yours"}`}.`
    : undefined;
  const beforeFigures = Boolean(table?.before && table.figuresFrom && table.before.from < table.figuresFrom);

  const save = () => {
    if (!spec) return;
    if (id && mine && view.data) update.mutate({ name: view.data.name, spec });
    else setNaming("new");
  };

  const named = (name: string) => {
    if (!spec) return;
    if (naming === "rename" && id) {
      update.mutate({ name, spec: saved ?? spec }, { onSuccess: () => setNaming(null) });
      return;
    }
    create.mutate(
      { name, spec },
      { onSuccess: (v) => { setNaming(null); void navigate({ to: "/$market/reports/$id", params: { market: market.code, id: v.id }, search: {} }); } },
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow={
          <Link to="/$market/reports" params={{ market: market.code }} className="hover:text-ink">
            Reports ›
          </Link>
        }
        title={title}
        description={description}
        actions={
          spec && (
            <>
              {naming === null && (!id || mine || changed) && (
                <Button variant={changed ? "primary" : "secondary"} onClick={save} disabled={!changed || update.isPending}>
                  {id && !mine ? "Save as yours" : "Save"}
                </Button>
              )}
              <Menu>
                <MenuTrigger asChild>
                  <Button>
                    <DownloadSimple />
                    Export
                  </Button>
                </MenuTrigger>
                <MenuContent>
                  <MenuItem asChild>
                    <a href={reportPath(market.code, spec, "export", { format: "xlsx", name: title })} download>
                      Excel workbook
                    </a>
                  </MenuItem>
                  <MenuItem asChild>
                    <a href={reportPath(market.code, spec, "export", { format: "csv", name: title })} download>
                      CSV file
                    </a>
                  </MenuItem>
                </MenuContent>
              </Menu>
              {id && view.data && (
                <Menu>
                  <MenuTrigger asChild>
                    <Button variant="ghost" aria-label="More">
                      <DotsThree />
                    </Button>
                  </MenuTrigger>
                  <MenuContent>
                    <MenuItem onSelect={() => setNaming("new")}>Save as a new report</MenuItem>
                    {mine && <MenuItem onSelect={() => setNaming("rename")}>Rename</MenuItem>}
                    {market.can.share && mine && !view.data.shared && <MenuItem onSelect={() => share.mutate(true)}>Share with everyone in {market.name}</MenuItem>}
                    {market.can.share && view.data.shared && <MenuItem onSelect={() => share.mutate(false)}>Stop sharing</MenuItem>}
                    {(mine || (view.data.shared && market.can.share)) && (
                      <>
                        <MenuSeparator />
                        <MenuItem onSelect={() => setDeleting(true)} className="text-danger data-[highlighted]:text-danger">
                          Delete
                        </MenuItem>
                      </>
                    )}
                  </MenuContent>
                </Menu>
              )}
            </>
          )
        }
      />

      {naming && (
        <NameForm
          initial={naming === "rename" ? (view.data?.name ?? "") : id ? (view.data?.name ?? "") : starter?.name ?? ""}
          label={naming === "rename" ? "Rename" : "Save"}
          pending={create.isPending || update.isPending}
          error={(naming === "rename" ? update.error : create.error)?.message ?? null}
          onSave={named}
          onCancel={() => setNaming(null)}
        />
      )}
      {(update.isError && naming === null) || share.isError ? (
        <p className="bg-danger-soft px-3 py-2 text-sm text-danger">{(update.error ?? share.error)?.message}</p>
      ) : null}
      {view.data?.shared && <p className="text-xs text-ink-3">{mine ? `Shared with everyone in ${market.name}.` : `Shared by ${view.data.ownerName}: changes you make here are yours to save as your own.`}</p>}

      {spec && c && <Controls spec={spec} catalogue={c} table={table} market={market.code} onChange={setSpec} />}

      {report.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">This report cannot run: {report.error.message}</p>}
      {beforeFigures && table?.figuresFrom && <p className="text-xs text-ink-3">Sales has figures from {day(table.figuresFrom)}: the days before it are empty.</p>}
      {!table && !report.isError && <div className="h-60 bg-surface-2" aria-busy="true" />}
      {table && c && <Figures key={JSON.stringify(table.spec)} table={table} catalogue={c} currency={market.currency} market={market.code} />}

      <ConfirmDialog
        open={deleting}
        title={`Delete the report ${view.data?.name ?? ""}?`}
        description={view.data?.shared ? "It is shared: it goes for everyone in the market. The figures stay in Sales." : "Only the saved definition goes; the figures stay in Sales."}
        confirmLabel="Delete"
        tone="danger"
        pending={remove.isPending}
        error={remove.error?.message ?? null}
        onCancel={() => setDeleting(false)}
        onConfirm={() =>
          id && remove.mutate(id, { onSuccess: () => void navigate({ to: "/$market/reports", params: { market: market.code } }) })
        }
      />
    </div>
  );
}

function NameForm({ initial, label, pending, error, onSave, onCancel }: { initial: string; label: string; pending: boolean; error: string | null; onSave: (name: string) => void; onCancel: () => void }) {
  const [name, setName] = useState(initial);
  useEffect(() => {
    const close = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onCancel]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) onSave(name.trim());
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2 border-y border-line-strong bg-canvas px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input autoFocus aria-label="The report's name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Weekly scorecard" className="max-w-xs" aria-invalid={Boolean(error)} />
        <Button type="submit" variant="primary" disabled={pending || !name.trim()}>
          {label}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </form>
  );
}

/** The definition as controls: rows, a second dimension, the period, the comparison, across; then the measures and filters. */
function Controls({ spec, catalogue, table, market, onChange }: { spec: ReportSpec; catalogue: ReportCatalogue; table: ReportTable | undefined; market: string; onChange: (spec: ReportSpec) => void }) {
  const [editing, setEditing] = useState<ReportDimension | null>(null);
  const dimensions = catalogue.dimensions;
  const label = (d: ReportDimension) => dimensions.find((x) => x.key === d)?.label ?? d;
  const measureLabel = (m: string) => catalogue.measures.find((x) => x.key === m)?.label ?? m;
  const across = spec.across !== "None";
  const addable = catalogue.measures.filter((m) => !spec.measures.includes(m.key) && (m.key !== "Attainment" || attainmentFits(spec)));
  const filterable = dimensions.filter((d) => d.filterable && !spec.filters.some((f) => f.dimension === d.key));
  const names = new Map((table?.filters ?? []).flatMap((f) => f.values.map((v) => [`${f.dimension}:${v.code}`, v.name] as const)));
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select aria-label="Rows" value={spec.rows} onChange={(e) => onChange({ ...spec, rows: e.target.value as ReportDimension })} className="w-auto">
          {dimensions.map((d) => (
            <option key={d.key} value={d.key}>
              By {d.label.toLowerCase()}
            </option>
          ))}
        </Select>
        <Select aria-label="Then by" value={spec.then ?? ""} onChange={(e) => onChange({ ...spec, then: (e.target.value || null) as ReportDimension | null })} className="w-auto">
          <option value="">No second level</option>
          {dimensions.filter((d) => d.key !== spec.rows).map((d) => (
            <option key={d.key} value={d.key}>
              Then by {d.label.toLowerCase()}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Period"
          value={spec.period}
          onChange={(e) => {
            const period = e.target.value as ReportSpec["period"];
            onChange(period === "Custom" ? { ...spec, period, from: spec.from ?? `${today.slice(0, 8)}01`, to: spec.to ?? today } : { ...spec, period });
          }}
          className="w-auto"
        >
          {catalogue.periods.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </Select>
        {spec.period === "Custom" && (
          <>
            <Input type="date" aria-label="First day" value={spec.from ?? ""} max={spec.to ?? undefined} onChange={(e) => e.target.value && onChange({ ...spec, from: e.target.value })} className="w-auto" />
            <Input type="date" aria-label="Last day" value={spec.to ?? ""} min={spec.from ?? undefined} onChange={(e) => e.target.value && onChange({ ...spec, to: e.target.value })} className="w-auto" />
          </>
        )}
        <Select aria-label="Compare with" value={spec.compare} onChange={(e) => onChange({ ...spec, compare: e.target.value as ReportSpec["compare"], across: "None" })} className="w-auto">
          {catalogue.compares.map((x) => (
            <option key={x.key} value={x.key}>
              {x.label}
            </option>
          ))}
        </Select>
        <Select aria-label="Across" value={spec.across} onChange={(e) => onChange({ ...spec, across: e.target.value as ReportSpec["across"] })} className="w-auto">
          {catalogue.across.map((x) => (
            <option key={x.key} value={x.key} disabled={x.key !== "None" && (isTime(spec.rows) || isTime(spec.then))}>
              {x.label}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {spec.measures.map((m) => (
          <Button key={m} size="sm" aria-label={`Remove ${measureLabel(m)}`} disabled={spec.measures.length === 1} onClick={() => onChange({ ...spec, measures: spec.measures.filter((x) => x !== m) })}>
            {measureLabel(m)}
            {spec.measures.length > 1 && <X />}
          </Button>
        ))}
        {!across && addable.length > 0 && spec.measures.length < 8 && (
          <Select aria-label="Add a measure" value="" onChange={(e) => e.target.value && onChange({ ...spec, measures: [...spec.measures, e.target.value as ReportSpec["measures"][number]] })} className="h-8 w-auto text-sm">
            <option value="">Add a measure</option>
            {addable.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </Select>
        )}
        {across && <span className="text-xs text-ink-3">With weeks, months or quarters as columns, a report shows one measure.</span>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {spec.filters.map((f) => (
          <Button key={f.dimension} size="sm" variant={editing === f.dimension ? "secondary" : "ghost"} aria-expanded={editing === f.dimension} onClick={() => setEditing(editing === f.dimension ? null : f.dimension)} className="max-w-full">
            <span className="truncate">
              {label(f.dimension)}: {f.values.map((v) => names.get(`${f.dimension}:${v}`) ?? (v || "none")).join(", ")}
            </span>
          </Button>
        ))}
        {filterable.length > 0 && (
          <Select aria-label="Add a filter" value="" onChange={(e) => e.target.value && setEditing(e.target.value as ReportDimension)} className="h-8 w-auto text-sm">
            <option value="">Add a filter</option>
            {filterable.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </Select>
        )}
      </div>

      {editing && (
        <FilterEditor
          key={editing}
          market={market}
          dimension={editing}
          label={label(editing)}
          showsCode={dimensions.find((d) => d.key === editing)?.showsCode ?? false}
          current={spec.filters.find((f) => f.dimension === editing)?.values ?? []}
          names={names}
          onApply={(values) => {
            const others = spec.filters.filter((f) => f.dimension !== editing);
            onChange({ ...spec, filters: values.length > 0 ? [...others, { dimension: editing, values }] : others });
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** A filter's values to tick: every one for a short list, a search for customers and items. */
function FilterEditor({
  market, dimension, label, showsCode, current, names, onApply, onClose,
}: {
  market: string; dimension: ReportDimension; label: string; showsCode: boolean; current: string[]; names: Map<string, string>;
  onApply: (values: string[]) => void; onClose: () => void;
}) {
  const searched = dimension === "Customer" || dimension === "Item";
  const [q, setQ] = useState("");
  const typed = useDebounced(q);
  const options = useReportValues(market, dimension, searched ? typed : "");
  const [picked, setPicked] = useState(() => new Map(current.map((v) => [v, names.get(`${dimension}:${v}`) ?? v])));

  useEffect(() => {
    const close = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);

  const shown: ReportValue[] = searched
    ? [...[...picked].map(([code, name]) => ({ code, name })), ...(options.data ?? []).filter((v) => !picked.has(v.code))]
    : (options.data ?? []);
  const toggle = (v: ReportValue, on: boolean) =>
    setPicked((p) => {
      const next = new Map(p);
      if (on) next.set(v.code, v.name);
      else next.delete(v.code);
      return next;
    });

  return (
    <div className="flex flex-col gap-3 border-y border-line-strong bg-canvas px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-ink">{label}</span>
        {searched && (
          <Input autoFocus aria-label={`Search ${label.toLowerCase()}s`} placeholder={dimension === "Customer" ? "Nile Bakery or CH000182" : "Croissant or I000400"} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        )}
      </div>
      {options.isError && <p className="text-xs text-danger">{options.error.message}</p>}
      {shown.length === 0 && !options.isFetching && <p className="text-sm text-ink-3">{searched ? "Type a name or a number." : "Nothing to choose."}</p>}
      {shown.length > 0 && (
        <div className="grid max-h-72 grid-cols-1 gap-x-6 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((v) => (
            <Checkbox
              key={v.code || "none"}
              label={v.name}
              description={showsCode && v.code ? <span className="font-mono">{v.code}</span> : undefined}
              checked={picked.has(v.code)}
              onChange={(e) => toggle(v, e.target.checked)}
            />
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => onApply([...picked.keys()])} disabled={picked.size === 0 && current.length === 0}>
          {picked.size === 0 && current.length > 0 ? "Remove the filter" : "Apply"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The report's table: the rows' names, then each figure column. The first figure draws its size down the rows (a
 * measure across measures; not across the calendar). Columns after the first give way on smaller screens: the
 * comparison first, the other measures below `md`.
 */
function Figures({ table, catalogue, currency, market }: { table: ReportTable; catalogue: ReportCatalogue; currency: string; market: string }) {
  const dims = table.columns.filter((c) => c.kind === "dimension");
  const figures = table.columns.filter((c) => c.kind !== "dimension");
  const showsCode = (i: number) => catalogue.dimensions.find((d) => d.key === (i === 0 ? table.spec.rows : table.spec.then))?.showsCode ?? false;
  const columns: Record<string, (r: ReportRow) => string | number> = {};
  dims.forEach((c, i) => (columns[c.key] = (r) => r.labels[i]));
  figures.forEach((c, i) => (columns[c.key] = (r) => r.values[i] ?? Number.NEGATIVE_INFINITY));
  const sorted = useSortedRows(table.rows, "", columns);
  const across = table.spec.across !== "None";
  const barred = !across && figures[0]?.kind === "measure" && figures[0].format !== "decimal";
  const largest = barred ? Math.max(0, ...table.rows.map((r) => r.values[0] ?? 0)) : 0;
  const bar = (v: number | null) => (figures[0]?.format === "percent" ? (v ?? 0) : largest > 0 ? Math.max(0, v ?? 0) / largest : 0);
  // With more than three measures the period before stays in the export: on screen each measure keeps its change.
  const hidden = (c: ReportColumn) => c.kind === "before" && table.spec.measures.length > 3;
  const from = (c: ReportColumn, i: number): "md" | "lg" | "xl" | undefined =>
    i === 0 || c.kind === "total" ? undefined : c.kind === "before" ? "xl" : c.kind === "change" ? "lg" : "md";
  const linked = table.spec.rows === "Customer";

  if (!table.everyone && table.codes.length === 0) {
    return <EmptyState icon={FileMagnifyingGlass} title="No salesperson code is linked to you" description="Reports show your own book once the People page links your BC code to you." />;
  }
  if (table.rows.length === 0) {
    return <EmptyState icon={FileMagnifyingGlass} title="No sales in these days" description="Try another period, or fewer filters." />;
  }

  return (
    <div className="flex flex-col gap-2">
      <Table density="compact">
        <THead>
          <TR>
            {dims.map((c, i) => (
              <TH key={c.key} sort={sorted.header(c.key)} className={i > 0 ? "whitespace-nowrap" : undefined}>
                {c.label}
              </TH>
            ))}
            {figures.map((c, i) =>
              hidden(c) ? null : (
                <TH key={c.key} from={from(c, i)} bar={i === 0 && barred} sort={sorted.header(c.key, "desc")} className={cn("text-end", c.format === "money" && "whitespace-nowrap")}>
                  {header(c, currency, table.spec.across)}
                </TH>
              ),
            )}
          </TR>
        </THead>
        <TBody>
          {sorted.rows.map((r) => (
            <TR key={r.keys.join("|")} linked={linked && r.keys[0] !== ""}>
              {dims.map((c, i) => (
                <TD key={c.key} className={i === 0 ? "w-full min-w-[160px] max-w-0" : "max-w-[16rem]"}>
                  {i === 0 && linked && r.keys[0] !== "" ? (
                    <Link to="/$market/customers/$no" params={{ market, no: r.keys[0] }} className="block truncate">
                      {r.labels[0]}
                    </Link>
                  ) : (
                    <span className="block truncate">{r.labels[i]}</span>
                  )}
                  {showsCode(i) && r.keys[i] !== "" && <span className="block truncate font-mono text-xs text-ink-3">{r.keys[i]}</span>}
                </TD>
              ))}
              {figures.map((c, i) =>
                hidden(c) ? null : (
                  <TDNum key={c.key} from={from(c, i)} bar={i === 0 && barred ? bar(r.values[i]) : undefined} className={cn((c.kind === "before" || c.kind === "change") && "text-ink-2")}>
                    {figure(c, r.values[i])}
                  </TDNum>
                ),
              )}
            </TR>
          ))}
        </TBody>
        <TFoot>
          <TR>
            {dims.map((c, i) => (
              <TD key={c.key}>{i === 0 ? "Total" : ""}</TD>
            ))}
            {figures.map((c, i) =>
              hidden(c) ? null : (
                <TDNum key={c.key} from={from(c, i)} bar={i === 0 && barred ? null : undefined}>
                  {figure(c, table.total[i], true)}
                </TDNum>
              ),
            )}
          </TR>
        </TFoot>
      </Table>
      {table.truncated && (
        <p className="text-xs text-ink-3">
          The first {formatCount(table.rows.length)} of {formatCount(table.rowCount)} rows; the total and the export count every row.
        </p>
      )}
    </div>
  );
}
